import { useState, useMemo, useCallback } from 'react';
import { DeviceTelemetry } from './DeviceTelemetryApi';
import { DeviceState } from './DeviceStateApi';
import { DeviceTypeEnum, isOffsetSupported } from './DeviceTypeEnum';

export function useDeviceCounters() {
  const [totalKm, setTotalKm] = useState<number>();
  const [distanceOffset, setDistanceOffset] = useState<number>();
  const [kmInitialValue, setKmInitialValue] = useState<number>();

  const [totalRun1, setTotalRun1] = useState<number>();
  const [hoursOffset, setHoursOffset] = useState<number>();
  const [run1InitialValue, setRun1InitialValue] = useState<number>();
  const [run1, setRun1] = useState<number>();

  const [deviceTypeSupportsOffsets, setDeviceTypeSupportsOffsets] = useState<boolean>();

  const initializeCounters = useCallback((
    telemetry: DeviceTelemetry,
    state: DeviceState,
    deviceType: DeviceTypeEnum
  ) => {
    const reportedKmOffset = Number(state.reported.sensor?.distance?.offset) || 0;
    const reportedHoursOffset = Number(state.reported.io?.input1?.offset) || 0;

    const rawKm = Number(telemetry.sensor?.distance?.value) || 0;
    const rawRun1 = Number(telemetry.io?.input1?.activeTime) || 0;

    setDistanceOffset(reportedKmOffset);
    setHoursOffset(reportedHoursOffset);
    setKmInitialValue(rawKm);
    setTotalKm(rawKm + reportedKmOffset);
    setRun1InitialValue(rawRun1);
    setTotalRun1(rawRun1 + reportedHoursOffset);
    setRun1(rawRun1);
    setDeviceTypeSupportsOffsets(isOffsetSupported(deviceType));
  }, []);

  const updateOffsetsFromState = useCallback((state: DeviceState) => {
    const reportedKmOffset = Number(state.reported.sensor?.distance?.offset) || 0;
    const reportedHoursOffset = Number(state.reported.io?.input1?.offset) || 0;
    setDistanceOffset(reportedKmOffset);
    setHoursOffset(reportedHoursOffset);
  }, []);

  const getCalculatedOffsets = useCallback(() => {
    return {
      distanceOffset: (totalKm !== undefined && kmInitialValue !== undefined)
        ? totalKm - kmInitialValue
        : undefined,
      hoursOffset: (totalRun1 !== undefined && run1InitialValue !== undefined)
        ? totalRun1 - run1InitialValue
        : undefined,
    };
  }, [totalKm, kmInitialValue, totalRun1, run1InitialValue]);

  return useMemo(() => ({
    totalKm, setTotalKm,
    distanceOffset, setDistanceOffset,
    kmInitialValue, setKmInitialValue,
    totalRun1, setTotalRun1,
    hoursOffset, setHoursOffset,
    run1InitialValue, setRun1InitialValue,
    run1, setRun1,
    deviceTypeSupportsOffsets, setDeviceTypeSupportsOffsets,
    initializeCounters,
    updateOffsetsFromState,
    getCalculatedOffsets,
  }), [
    totalKm, distanceOffset, kmInitialValue,
    totalRun1, hoursOffset, run1InitialValue, run1,
    deviceTypeSupportsOffsets,
    initializeCounters, updateOffsetsFromState, getCalculatedOffsets,
  ]);
}
